import React, { useEffect, useRef, useState, useCallback } from 'react';
import Header from './Header';
import CarPreview from './CarPreview';
import ViewSelector from './ViewSelector';
import GraphicsSidebar from './GraphicsSidebar';
import { useCarCustomizer } from './useCarCustomizer';
import CarModelSelector from './CarModelSelector';
import OrderSummary from './OrderSummary';
import { toast } from 'react-hot-toast';
import ScreenSizeWarning from './ScreenSizeWarning';
import { preloadAllCarAssets, preloadCarAssets } from '../../utils/imagePreloader';
import { getCarModels, getCarModelById } from '../../services/carModelsService';
import ProductTour from './ProductTour';
import { useCart } from '../../context/CartContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { logger } from '../../utils/logger';

const DekalioCustomizer = () => {
  const [selectedCar, setSelectedCar] = useState(null);
  
  const {
    selectedColor,
    setSelectedColor,
    helfoliering,
    setHelfoliering,
    HELFOLIERING_FEE,
    selectedView,
    setSelectedView,
    uploadedFiles,
    setUploadedFiles,
    placedGraphics,
    setPlacedGraphics,
    price,
    carDimensions,
    handleFileUpload,
    placeGraphic,
    updateGraphicPosition,
    updateGraphicSize,
    updateGraphicRotation,
    deleteGraphic,
    updateGraphicZIndex,
    removeUploadedFile,
    placeText,
    placeShape,
    undo,
    redo,
    canUndo,
    canRedo
  } = useCarCustomizer(selectedCar?.id, selectedCar);

  // Shape drawing state
  const [activeShapeTool, setActiveShapeTool] = useState(null);
  const [shapeProperties, setShapeProperties] = useState({
    fill: '#000000',
    stroke: 'transparent',
    strokeWidth: 0,
    opacity: 1
  });

  const handleShapeToolSelect = (toolId) => {
    setActiveShapeTool(activeShapeTool === toolId ? null : toolId);
  };

  const handleShapePropertyChange = (newProperties) => {
    setShapeProperties(prev => ({ ...prev, ...newProperties }));
    
    // If a shape is selected, update its properties immediately
    if (carPreviewRef.current?.updateSelectedShapeProperties) {
      carPreviewRef.current.updateSelectedShapeProperties(newProperties);
    }
  };

  const handleShapeSelect = (shapeObject) => {
    console.log('Shape selected:', shapeObject);
    
    if (!shapeObject) {
      setSelectedShape(null);
      return;
    }
    
    setSelectedShape(shapeObject);
    
    // If shape was just drawn, deactivate the tool for better UX
    if (shapeObject.justDrawn) {
      setActiveShapeTool(null);
    }
    
    // Update shape properties to match selected shape
    setShapeProperties({
      fill: shapeObject.fill || '#000000',
      stroke: 'transparent',
      strokeWidth: 0,
      opacity: 1
    });
  };

  const { getDesignState, addToCart, updateCartItem } = useCart();
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const carPreviewRef = useRef(null);
  const [selectedText, setSelectedText] = useState(null);
  const [selectedShape, setSelectedShape] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [isScreenTooSmall, setIsScreenTooSmall] = useState(false);
  const [isPreloading, setIsPreloading] = useState(true);
  const [showTour, setShowTour] = useState(false);
  const [handleFileCheck, setHandleFileCheck] = useState(null);

  useEffect(() => {
    logger.log('Selected car updated:', selectedCar);
  }, [selectedCar]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsScreenTooSmall(window.innerWidth < 1024); // Show warning on screens smaller than 1024px
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  // Preload all assets when component mounts
  useEffect(() => {
    const preloadAssets = async () => {
      const carModels = await getCarModels();
      await preloadAllCarAssets(carModels);
      setIsPreloading(false);
    };
    preloadAssets();
  }, []);

  useEffect(() => {
    // Check if this is the first visit
    const hasSeenTour = localStorage.getItem('hasSeenProductTour');
    if (!hasSeenTour && currentStep === 2) { // Only show tour after car selection
      setShowTour(true);
    }
  }, [currentStep]);

  // Handle pre-selected car model from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const modelId = params.get('model');
    
    if (modelId && !selectedCar) {
      const loadCarModel = async () => {
        const carModel = await getCarModelById(modelId);
        
        if (carModel) {
          setSelectedCar(carModel);
          setCurrentStep(2); // Skip car selection step
        }
      };
      
      loadCarModel();
    }
  }, [location, selectedCar]);

  useEffect(() => {
    // Check if we're in edit mode by looking for the edit parameter
    const params = new URLSearchParams(location.search);
    const editItemId = params.get('edit');
    
    if (editItemId) {
      const designState = getDesignState(editItemId);
      if (designState) {
        console.log('Restoring design state:', designState);
        setIsEditMode(true);
        setEditingItemId(editItemId);
        
        // Restore the complete design state
        setSelectedCar(designState.selectedCar);
        setSelectedColor(designState.selectedColor || '#FFFFFF');
        setHelfoliering(designState.helfoliering === true);
        
        // First restore the uploaded files
        const restoredFiles = [];
        if (designState.placedGraphics) {
          // Extract unique files from placed graphics
          const uniqueFiles = new Set();
          Object.values(designState.placedGraphics).flat().forEach(graphic => {
            if (graphic.type === 'image' && !uniqueFiles.has(graphic.originalFileId)) {
              uniqueFiles.add(graphic.originalFileId);
              restoredFiles.push({
                id: graphic.originalFileId,
                name: graphic.name,
                src: graphic.src,
                needsVectorization: graphic.needsVectorization
              });
            }
          });
        }
        
        // Set the uploaded files
        setUploadedFiles(restoredFiles);
        
        // Then restore the placed graphics
        setPlacedGraphics(designState.placedGraphics || {});
        
        setCurrentStep(2); // Skip car selection step
      }
    }
  }, [location, getDesignState]);

  if (isScreenTooSmall) {
    return <ScreenSizeWarning />;
  }

  if (isPreloading) {
    return <div className="min-h-screen bg-background flex items-center justify-center">
      <div>Laddar...</div>
    </div>;
  }

  const handleTextUpdate = (textConfig) => {
    console.log('Handling text update:', textConfig);
    
    // If it's just a preview, don't update the state
    if (textConfig.isPreview) {
      // Only update the canvas preview
      return;
    }

    // Otherwise, update the actual state
    setPlacedGraphics(prev => {
      const viewGraphics = prev[selectedView] || [];
      const updated = {
        ...prev,
        [selectedView]: viewGraphics.map(item => 
          item.id === selectedText.id ? {
            ...item,
            text: textConfig.text,
            font: textConfig.font,
            color: textConfig.color,
            align: textConfig.align
          } : item
        )
      };
      console.log('Updated placedGraphics:', updated);
      return updated;
    });

    // Only clear selectedText if we're not keeping edit mode
    if (!textConfig.keepEditMode) {
      setSelectedText(null);
    }
  };

  const handleCarSelection = async (car) => {
    console.log('Car selected:', car);
    
    // Start preloading assets immediately
    const carModels = await getCarModels();
    await preloadCarAssets(car.id, carModels);
    
    setSelectedCar(car);
    setCurrentStep(2);
  };

  const handleOrderSubmit = (orderDetails) => {
    if (isEditMode && editingItemId) {
      updateCartItem(editingItemId, orderDetails);
      navigate('/cart');
    } else {
      addToCart(orderDetails);
    }
  };

  const hasPlacedGraphics = () => {
    return Object.values(placedGraphics).some(viewGraphics => viewGraphics.length > 0);
  };

  const handleDuplicateGraphic = (originalId, newId, view) => {
    const originalGraphic = placedGraphics[view].find(g => g.id === originalId);
    if (!originalGraphic) return;

    const newGraphic = {
      ...originalGraphic,
      id: newId,
      x: originalGraphic.x + 20,
      y: originalGraphic.y + 20
    };

    setPlacedGraphics(prev => ({
      ...prev,
      [view]: [...prev[view], newGraphic]
    }));
  };

  const handleDuplicateDesign = () => {
    // Keep the current graphics but reset the car selection
    setSelectedCar(null);
    setCurrentStep(1);
  };

  const handleNewDesign = () => {
    // Reset everything
    setSelectedCar(null);
    setPlacedGraphics({});
    setUploadedFiles([]);
    setCurrentStep(1);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <CarModelSelector onSelectModel={handleCarSelection} />;
      
      case 2:
        return selectedCar && (
          <main className="flex flex-col md:flex-row car-preview-wrapper bg-secondary/50 h-[calc(100vh-3.5rem)] overflow-hidden">
            <div className="flex-1 min-w-0 relative">
              <div className="w-full h-full max-w-full overflow-hidden">
                
                <CarPreview 
                  selectedColor={selectedColor}
                  selectedView={selectedView}
                  setSelectedView={setSelectedView}
                  placedGraphics={placedGraphics}
                  setPlacedGraphics={setPlacedGraphics}
                  updateGraphicPosition={updateGraphicPosition}
                  updateGraphicSize={updateGraphicSize}
                  updateGraphicRotation={updateGraphicRotation}
                  deleteGraphic={deleteGraphic}
                  updateGraphicZIndex={updateGraphicZIndex}
                  carDimensions={selectedCar?.dimensions || carDimensions}
                  placeText={placeText}
                  placeShape={placeShape}
                  activeShapeTool={activeShapeTool}
                  shapeProperties={shapeProperties}
                  ref={carPreviewRef}
                  selectedCarModel={selectedCar?.id}
                  carModelData={selectedCar}
                  onBackToCarSelection={() => setCurrentStep(1)}
                  onTextSelect={(textObject) => {
                    setSelectedText(textObject);
                  }}
                  onShapeSelect={handleShapeSelect}
                  duplicateGraphic={handleDuplicateGraphic}
                  undo={undo}
                  redo={redo}
                  canUndo={canUndo}
                  canRedo={canRedo}
                  handleFileCheck={handleFileCheck}
                />
              </div>
              <ViewSelector 
                selectedView={selectedView} 
                setSelectedView={setSelectedView} 
              />
            </div>
            <GraphicsSidebar 
              uploadedFiles={uploadedFiles}
              handleFileUpload={handleFileUpload}
              placeGraphic={placeGraphic}
              removeUploadedFile={removeUploadedFile}
              selectedColor={selectedColor}
              setSelectedColor={setSelectedColor}
              helfoliering={helfoliering}
              setHelfoliering={setHelfoliering}
              helfolieringFee={HELFOLIERING_FEE}
              price={price}
              placedGraphics={placedGraphics}
              setPlacedGraphics={setPlacedGraphics}
              onAddText={placeText}
              selectedText={selectedText}
              onUpdateText={handleTextUpdate}
              activeShapeTool={activeShapeTool}
              shapeProperties={shapeProperties}
              selectedShape={selectedShape}
              onShapeToolSelect={handleShapeToolSelect}
              onShapePropertyChange={handleShapePropertyChange}
              onNextStep={() => {
                if (hasPlacedGraphics() || helfoliering) {
                  // Deselect any active graphic/text before proceeding
                  if (carPreviewRef.current?.deselectActive) {
                    carPreviewRef.current.deselectActive();
                  }
                  setCurrentStep(3);
                } else {
                  toast.error('Lägg till minst en grafik eller välj helfoliering för att gå vidare');
                }
              }}
              onFileCheckReady={(fileCheckFunc) => setHandleFileCheck(() => fileCheckFunc)}
            />
          </main>
        );
      
      case 3:
        return (
          <OrderSummary
            selectedCar={selectedCar}
            selectedColor={selectedColor}
            helfoliering={helfoliering}
            helfolieringFee={HELFOLIERING_FEE}
            selectedView={selectedView}
            placedGraphics={placedGraphics}
            price={price}
            carDimensions={carDimensions}
            setSelectedView={setSelectedView}
            onComplete={handleNewDesign}
            onDuplicateDesign={handleDuplicateDesign}
            onSubmitOrder={handleOrderSubmit}
            isEditMode={isEditMode}
            onBackToDesign={() => setCurrentStep(2)}
          />
        );
      
      default:
        return null;
    }
  };

  const handleTourComplete = () => {
    setShowTour(false);
    localStorage.setItem('hasSeenProductTour', 'true');
  };

  const handleStepClick = (step) => {
    if (step === 1) {
      setCurrentStep(1);
    } else if (step === 2 && selectedCar) {
      setCurrentStep(2);
    } else if (step === 3 && selectedCar && (hasPlacedGraphics() || helfoliering)) {
      setCurrentStep(3);
    } else if (step === 3) {
      toast.error('Lägg till minst en grafik eller välj helfoliering för att gå vidare');
    }
  };

  return (
    <div className="min-h-screen bg-secondary/50">
      <Header 
        isEditMode={isEditMode}
        currentStep={currentStep}
        onStepClick={handleStepClick}
      />
      {renderStep()}
      {showTour && (
        <ProductTour
          onFinish={handleTourComplete}
          onComplete={handleTourComplete}
          onSkip={handleTourComplete}
          run={showTour}
        />
      )}
    </div>
  );
};

export default DekalioCustomizer;